import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useChannelPartners } from "@/hooks/useChannelPartners";
import { AddEditChannelPartnerSheet } from "@/components/tools/AddEditChannelPartnerSheet";
import { Users, Plus, Upload, Share2, Edit2, Trash2, Search, Download, Phone, MoreVertical, MapPin } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Connector } from "@/types";
import { exportVendors, generateVendorTemplate, parseExcelFile } from "@/services/excelExportService";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

interface VendorManagementProps {
  teamMemberFilter: string;
}

export function VendorManagement({ teamMemberFilter }: VendorManagementProps) {
  const { partners, loading, addPartner, updatePartner, deletePartner } = useChannelPartners();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Connector | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState<Connector | null>(null);

  const filteredVendors = partners.filter(v => {
    // Search filter
    const matchesSearch = v.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.whatsappNumber.includes(searchQuery) ||
      v.city?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Team member filter (from props)
    const matchesTeamMember = teamMemberFilter === "all" || v.createdBy === teamMemberFilter;
    
    return matchesSearch && matchesTeamMember;
  });

  const handleBulkImport = async (file: File) => {
    try {
      // TODO: Parse Excel file and import vendors in bulk
      // For now, show success message
      toast.success("Bulk import functionality coming soon");
      setBulkImportOpen(false);
    } catch (error) {
      toast.error("Failed to import vendors");
    }
  };

  const handleShareVendors = () => {
    if (filteredVendors.length === 0) {
      toast.error("No vendors to export");
      return;
    }
    try {
      exportVendors(filteredVendors as any);
      toast.success(`Exported ${filteredVendors.length} vendors to Excel`);
    } catch (error) {
      toast.error("Failed to export vendors");
      console.error("Export error:", error);
    }
  };

  const handleExportTemplate = () => {
    try {
      generateVendorTemplate();
      toast.success("Template downloaded successfully");
    } catch (error) {
      toast.error("Failed to generate template");
      console.error("Template error:", error);
    }
  };

  const confirmDelete = (vendor: Connector) => {
    setVendorToDelete(vendor);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!vendorToDelete) return;
    
    try {
      await deletePartner(vendorToDelete.id);
      toast.success("Vendor deleted successfully");
    } catch (error) {
      toast.error("Failed to delete vendor");
    } finally {
      setDeleteDialogOpen(false);
      setVendorToDelete(null);
    }
  };

  const handleSaveVendor = async (vendorData: Partial<Connector>) => {
    try {
      if (editingVendor) {
        await updatePartner(editingVendor.id, vendorData);
        toast.success("Vendor updated successfully");
      } else {
        await addPartner(vendorData as Omit<Connector, 'id'>);
        toast.success("Vendor added successfully");
      }
      setSheetOpen(false);
      setEditingVendor(null);
    } catch (error) {
      const isDuplicate = error instanceof Error && error.message.startsWith('DUPLICATE_CP:');
      toast.error(
        isDuplicate
          ? "A vendor with this WhatsApp number already exists in your list."
          : editingVendor ? "Failed to update vendor" : "Failed to add vendor"
      );
    }
  };

  const handleAddNew = () => {
    setEditingVendor(null);
    setSheetOpen(true);
  };

  const handleEdit = (vendor: Connector) => {
    setEditingVendor(vendor);
    setSheetOpen(true);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Vendor Management
          </CardTitle>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => setBulkImportOpen(true)}>
              <Upload className="h-4 w-4 mr-2" />
              Bulk Import
            </Button>
            <Button size="sm" onClick={handleShareVendors}>
              <Share2 className="h-4 w-4 mr-2" />
              Share
            </Button>
            <Button size="sm" onClick={handleAddNew}>
              <Plus className="h-4 w-4 mr-2" />
              Add Vendor
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search Filter */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search vendors..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Vendor List */}
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-muted/50 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : filteredVendors.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No vendors found</p>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="block md:hidden space-y-2 max-h-[400px] overflow-y-auto">
              {filteredVendors.map((vendor, index) => (
                <motion.div
                  key={vendor.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="border rounded-lg p-3 active:bg-muted/50 tap-highlight-green"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Avatar className="h-9 w-9 flex-shrink-0">
                        <AvatarFallback className="bg-green-500 text-white text-xs font-semibold">
                          {vendor.name.substring(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{vendor.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{vendor.whatsappNumber}</p>
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="h-9 w-9"
                        onClick={() => window.open(`tel:${vendor.whatsappNumber}`, '_self')}
                      >
                        <Phone className="h-4 w-4" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="icon" variant="ghost" className="h-9 w-9">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          <DropdownMenuItem onClick={() => handleEdit(vendor)}>
                            <Edit2 className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => confirmDelete(vendor)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs pt-2 border-t">
                    <div>
                      <p className="text-muted-foreground mb-0.5">City</p>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3 text-muted-foreground" />
                        <p className="font-medium truncate">{vendor.city || 'N/A'}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-0.5">Type</p>
                      <Badge variant="outline" className="text-[10px] w-fit">
                        {vendor.type}
                      </Badge>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Desktop List View */}
            <div className="hidden md:block space-y-2 max-h-[400px] overflow-y-auto">
              {filteredVendors.map((vendor, index) => (
                <motion.div
                  key={vendor.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium">{vendor.name}</p>
                      <Badge variant="outline" className="text-[10px]">
                        {vendor.type}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <span>{vendor.whatsappNumber}</span>
                      {vendor.city && (
                        <>
                          <span>•</span>
                          <span>{vendor.city}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleEdit(vendor)}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => confirmDelete(vendor)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          </>
        )}

        {/* Add/Edit Vendor Sheet */}
        <AddEditChannelPartnerSheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          partner={editingVendor}
          onSave={handleSaveVendor}
        />

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Vendor</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete {vendorToDelete?.name}? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Bulk Import Sheet */}
        <Sheet open={bulkImportOpen} onOpenChange={setBulkImportOpen}>
          <SheetContent side="bottom" className="h-[50vh]">
            <SheetHeader>
              <SheetTitle>Bulk Import Vendors</SheetTitle>
            </SheetHeader>
            <div className="mt-6 space-y-4">
              <div className="border-2 border-dashed rounded-lg p-6 text-center">
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-4">
                  Upload Excel file with vendor data (.xlsx, .xls, .csv)
                </p>
                <Input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => {
                    if (e.target.files?.[0]) {
                      handleBulkImport(e.target.files[0]);
                    }
                  }}
                />
              </div>
              <Button className="w-full" variant="outline" onClick={handleExportTemplate}>
                <Download className="h-4 w-4 mr-2" />
                Download Template
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </CardContent>
    </Card>
  );
}
